<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { definePage } from 'unplugin-vue-router/runtime'
  import alertIcon from '@assets/icons/alert-circle.svg'
  import chatIcon from '@assets/icons/consultation/chat.svg'
  import phoneIcon from '@assets/icons/consultation/phone.svg'
  import clockIcon from '@assets/icons/clock.svg'
  import backArrowIcon from '@assets/icons/consultation/back-arrow.svg'
  import calendarIcon from '@assets/icons/calendar.svg'
  import type { Consultation, Pagination } from '@/types'
  import { dateToLocal } from '@/utils/date.ts'
  import { useDisplay } from 'vuetify/framework'

  const { t } = useI18n()
  const { mobile } = useDisplay()

  definePage({
    meta: {
      authenticated: true,
      title: 'my_consultations',
    },
  })

  document.title = t('my_consultations')
  const page = ref(1)
  const hasMore = ref(true) // Assume there is content to load initially
  const fetchData = async (pageToFetch: number | undefined) => {
    try {
      const response = await useForm<Record<string, unknown>, Pagination<Consultation>>('get', `/consultations`, {
        sort: '-id',
        per_page: 5,
        page: pageToFetch,
        status: 'closed',
        accepted: 1,
        include: 'recommendation',
      }).submit()
      return response.data
    } catch (e) {
      console.error(e)
      return {
        data: [],
      }
    }
  }

  const consultations = ref<Consultation[]>([])

  const loadMoreConsultations = async (loader: {
    side: 'start' | 'end' | 'both'
    done: (status: 'error' | 'loading' | 'empty' | 'ok') => void
  }) => {
    if (!hasMore.value) {
      loader.done('empty')
      return
    }

    try {
      const response = await fetchData(page.value)

      consultations.value.push(...response!.data)

      hasMore.value = response!.data.length > 0
      page.value += 1

      loader.done(hasMore.value ? 'ok' : 'empty')

    } catch (error) {
      console.error('Failed to load consultations:', error)
      loader.done('error')
    }
  }
</script>

<template>
  <v-container
    class="px-4 bg-grey-900 rounded-1xl"
    :class="{
      'h-screen': $vuetify.display.smAndDown,
    }"
    max-width="700"
  >
    <v-card
      class="mx-auto bg-grey-900 pa-sm-4 pa-0"
      elevation="0"
    >
      <v-card-title class="font-weight-bold pa-0 mb-4" style="font-size: 1.125rem;">
        <div class="d-flex align-center ga-2">
          <v-btn
            class="p-0 m-0"
            :ripple="false"
            size="32"
            :to="{path:'/'}"
            variant="text"
          >
            <v-icon :icon="backArrowIcon" size="24" />
          </v-btn>
          {{ t('my_consultations') }}
        </div>
      </v-card-title>
      <v-infinite-scroll @load="loadMoreConsultations">
        <v-card
          v-for="consultation in consultations"
          :key="consultation.id"
          class="bg-login-bg pa-4 rounded-md mb-3 border-sm border-grey-800 border-opacity-100"
          elevation="0"
        >
          <v-card-title class="pa-0 text-caption font-weight-bold d-flex align-center">
            <span class="flex-grow-1 text-grey-50">
              {{ consultation.doctor_name || t('not_assigned_to_doctor') }}
            </span>
            <v-chip
              v-if="consultation.is_fulfilled === 0"
              class="text-caption px-2"
              color="red"
              density="compact"
              rounded="sm"
              variant="outlined"
            >
              {{ t('consultation_not_completed') }}
            </v-chip>
            <v-chip
              v-if="consultation.is_fulfilled === null || (consultation.is_fulfilled && !consultation.recommendation)"
              class="text-caption px-2 text-orange border-sm border-opacity-75 border-orange"
              density="compact"
              rounded="sm"
              variant="outlined"
            >
              {{ t('consultation_waiting_recommendation') }}
            </v-chip>
          </v-card-title>
          <v-card-text class="px-0 py-3 text-caption">
            {{ consultation.question }}
          </v-card-text>
          <v-card-actions class="pa-0">
            <div class="text-grey-400 text-caption d-flex align-center justify-center">
              <v-icon
                class="flex-shrink-0"
                color="grey-400"
                :icon="consultation.medium == 'chat' ? chatIcon : phoneIcon"
                size="14"
              />
              <span class="ms-1 flex-lg-shrink-1">{{
                consultation.medium == 'chat' ? t('consultation_medium_chat') : t('consultation_medium_gsm')
              }}</span>
            </div>
            <v-spacer />
            <div class="text-grey-400 text-caption d-flex align-center justify-center">
              <v-icon class="flex-shrink-0" color="grey-400" :icon="calendarIcon" size="14" />
              <span class="ms-1 flex-lg-shrink-1">{{ dateToLocal(consultation!.created_at!, 'keyboardDate') }}</span>
            </div>
            <div class="text-grey-400 text-caption d-flex align-center justify-center ms-2">
              <v-icon class="flex-shrink-0" color="grey-400" :icon="clockIcon" size="14" />
              <span class="ms-1 flex-lg-shrink-1">{{ dateToLocal(consultation!.created_at!, 'fullTime12h') }}</span>
            </div>
          </v-card-actions>
          <v-card-text class="mt-6 pa-0">
            <v-btn
              v-if="consultation.is_fulfilled === 0"
              v-track="{event:'initiate_consultation_clicked',eventData: {source: 'consultation_not_completed'}}"
              class="font-weight-bold"
              color="primary"
              flat
              height="44"
              :max-width="mobile ? undefined : 200"
              rounded="sm"
              :to="{ path: '/', query: { q: t('start_consultation_query') } }"
              width="100%"
            >
              {{ t('consultation_ask_again_btn') }}
            </v-btn>

            <v-btn
              v-if="consultation.is_fulfilled && consultation.recommendation"
              v-track="{event:'view_consultation_clicked',eventData: {id: consultation.id}}"
              class="font-weight-bold"
              color="grey-100"
              flat
              height="44"
              :max-width="mobile ? undefined : 200"
              rounded="sm"
              :to="{ name: 'profile-consultations-id', params: { id:consultation.id } }"
              variant="outlined"
              width="100%"
            >{{ t('consultation_show_recommendation_btn') }}
            </v-btn>

            <v-btn
              v-if="consultation.is_fulfilled === null || (consultation.is_fulfilled && !consultation.recommendation)"
              v-track="{event:'view_consultation_clicked',eventData: {id: consultation.id}}"
              block
              class="font-weight-bold"
              color="grey-100"
              flat
              height="44"
              rounded="sm"
              :to="{ name: 'profile-consultations-id', params: { id:consultation.id } }"
              variant="text"
            >
              {{ t('consultation_waiting_recommendation_btn') }}
            </v-btn>
          </v-card-text>
        </v-card>

        <template #loading>
          <div class="text-center my-8">
            <v-progress-circular color="primary" indeterminate />
          </div>
        </template>

        <template #empty>
          <v-card-text v-if="!consultations.length" class="text-center bg-background py-5 px-4 rounded-lg">
            <div class="bg-alpha-primary-1 pa-3 rounded-circle d-inline-flex justify-center align-center mx-auto ">
              <v-icon color="primary" :icon="alertIcon" size="32" />
            </div>
            <p class="font-weight-bold text-h6 mt-4">{{ t('you_dont_have_consultations') }}</p>
            <v-btn
              v-track="{event:'initiate_consultation_clicked',eventData: {source: 'consultations_list'}}"
              block
              class="font-weight-bold mt-4"
              color="primary"
              flat
              height="44"
              rounded="md"
              :to="{ path: '/', query: { q: t('start_consultation_query') } }"
            >{{ t('talk_to_doctor_now') }}
            </v-btn>
          </v-card-text>
        </template>
      </v-infinite-scroll>
    </v-card>
  </v-container>
</template>

<style lang="scss" scoped>
.v-card-actions {
  min-height: 20px;
}
</style>
